import { ChevronRightIcon, PlusIcon } from "@heroicons/react/20/solid";
import { CoverageStatusDisplayName } from "pmt-types/src/enums/CoverageStatus.ts";
import { ExcessLiabilityPolicy } from "pmt-types/src/models/ExcessLiabilityPolicy.ts";
import { GeneralLiabilityPolicy } from "pmt-types/src/models/GeneralLiabilityPolicy.ts";

import { Maybe } from "../../utils/types/maybe.ts";

type Props = {
  generalLiability: Maybe<GeneralLiabilityPolicy>;
  excessLiabilities: Maybe<ExcessLiabilityPolicy[]>;
  onAddExcessLiabilityPolicy: () => void;
  onEditExcessLiabilityPolicy: (index: number) => void;
  onAddGeneralLiabilityPolicy: () => void;
  onEditGeneralLiabilityPolicy: () => void;
};

export default function PolicyList({
  generalLiability,
  excessLiabilities,
  onAddExcessLiabilityPolicy,
  onEditExcessLiabilityPolicy,
  onAddGeneralLiabilityPolicy,
  onEditGeneralLiabilityPolicy,
}: Props) {
  if (generalLiability == null) {
    return (
      <div className="sm:col-span-full text-center">
        <h3 className="mt-2 text-sm font-semibold text-gray-900">
          No coverage
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Get started by adding a general liability policy.
        </p>
        <div className="mt-6">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
            onClick={onAddGeneralLiabilityPolicy}
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Add general liability policy
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sm:col-span-full">
      <ul
        role="list"
        className="divide-y divide-gray-200 overflow-hidden bg-white shadow-sm ring-1 ring-gray-200 sm:rounded-xl"
      >
        {
          <li className="relative flex justify-between gap-x-6 px-4 py-5 hover:bg-gray-50 sm:px-6 cursor-pointer">
            <div className="flex min-w-0 gap-x-4">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-gray-900">
                  <a
                    onClick={() => {
                      onEditGeneralLiabilityPolicy();
                    }}
                  >
                    <span className="absolute inset-x-0 -top-px bottom-0" />
                    {generalLiability.policyNumber}
                  </a>
                </p>
                <p className="mt-1 flex text-xs leading-5 text-gray-500">
                  General Liability
                </p>
                <p className="mt-1 flex text-xs leading-5 text-gray-500">
                  {generalLiability.insurer}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-x-4">
              <div className="hidden sm:flex sm:flex-col sm:items-end">
                <p className="text-sm leading-6 text-gray-900">
                  {CoverageStatusDisplayName[generalLiability.status]}
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Effective{" "}
                  <time dateTime={generalLiability.effectiveDate?.toString()}>
                    {generalLiability.effectiveDate?.toString() ?? "-"}
                  </time>
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Expires{" "}
                  <time dateTime={generalLiability.expirationDate?.toString()}>
                    {generalLiability.expirationDate?.toString() ?? "-"}
                  </time>
                </p>
              </div>
              <ChevronRightIcon
                className="h-5 w-5 flex-none text-gray-400"
                aria-hidden="true"
              />
            </div>
          </li>
        }
        {(excessLiabilities ?? []).map((policy, index) => (
          <li
            key={index}
            className="relative flex justify-between gap-x-6 px-4 py-5 hover:bg-gray-50 sm:px-6 cursor-pointer"
          >
            <div className="flex min-w-0 gap-x-4">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-gray-900">
                  <a
                    onClick={() => {
                      onEditExcessLiabilityPolicy(index);
                    }}
                  >
                    <span className="absolute inset-x-0 -top-px bottom-0" />
                    {policy.policyNumber}
                  </a>
                </p>
                <p className="mt-1 flex text-xs leading-5 text-gray-500">
                  Excess Liability&nbsp;
                  {index + 1}
                </p>
                <p className="mt-1 flex text-xs leading-5 text-gray-500">
                  {policy.insurer}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-x-4">
              <div className="hidden sm:flex sm:flex-col sm:items-end">
                <p className="text-sm leading-6 text-gray-900">
                  {CoverageStatusDisplayName[policy.status]}
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Effective{" "}
                  <time dateTime={policy.effectiveDate?.toString()}>
                    {policy.effectiveDate?.toString() ?? "-"}
                  </time>
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Expires{" "}
                  <time dateTime={policy.expirationDate?.toString()}>
                    {policy.expirationDate?.toString() ?? "-"}
                  </time>
                </p>
              </div>
              <ChevronRightIcon
                className="h-5 w-5 flex-none text-gray-400"
                aria-hidden="true"
              />
            </div>
          </li>
        ))}
      </ul>
      <a
        className="mt-2 flex w-full items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 focus-visible:outline-offset-0 cursor-pointer"
        onClick={onAddExcessLiabilityPolicy}
      >
        <PlusIcon
          className="h-5 w-5 mr-1 flex-none text-sky-500"
          aria-hidden="true"
        />
        Add excess liability
      </a>
    </div>
  );
}
